import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { deletePosOrder, posDateTimeText } from '../utils/posUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import {
  POS_DELETE_REASON_MIN_LENGTH,
  POS_DELETE_REASON_MAX_LENGTH,
  POS_DELETE_REASON_PRESETS,
} from '../constants/posConst';
import '../css/Pos.css';

/**
 * Asks why a sale is being deleted, then deletes it.
 *
 * The reason is REQUIRED before the Delete button wakes up: a sale vanishing
 * from the takings with no explanation is exactly what an owner reading the
 * books later cannot reconcile. The preset chips fill the box rather than
 * replacing it, so a cashier can tap "Wrong item entered" and add which one.
 *
 * A SOFT delete: the sale's status changes to deleted and nothing else moves —
 * the order is kept as it was taken and stock is not touched (see
 * deletePosOrder).
 *
 * COMMITS ON ITS OWN, like PosDeleteProductModal: the caller is told only that
 * the sale is gone. Closes on the × or Cancel only — never on a backdrop tap
 * (CLAUDE.md > Behavior).
 *
 * @param {object} props
 * @param {object} props.order - the sale to delete; needs `id`, `items`, `total`
 * @param {(result: {deletion: object}) => void} props.onDeleted
 * @param {() => void} props.onClose - dismissed without deleting
 * @returns {JSX.Element}
 */
function PosDeleteSaleModal({ order, onDeleted, onClose }) {
  const { config, clientId } = useConfig();
  const { staffEmail } = usePosSession();
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= POS_DELETE_REASON_MIN_LENGTH
    && trimmed.length <= POS_DELETE_REASON_MAX_LENGTH;
  const payment = order?.paymentLabel || order?.paymentMethod || '';

  /**
   * Deletes the sale with the typed reason; stays open with the error on failure.
   * @returns {Promise<void>}
   */
  const handleDelete = async () => {
    if (deleting || !reasonValid) return;
    setDeleting(true);
    setError('');
    try {
      const result = await deletePosOrder(clientId, order, trimmed, staffEmail);
      onDeleted(result);
    } catch (err) {
      setError(describeWriteError(err, { what: 'the deletion of this sale', path: `posTenants/${clientId}/orders/${order?.id}` }));
      setDeleting(false);
    }
  };

  return createPortal(
    <div className="pos-modal-overlay pos-modal-overlay-stacked">
      <div className="pos-modal pos-modal-narrow" role="dialog" aria-modal="true" aria-label="Delete sale">
        <header className="pos-modal-head">
          <h3>Delete sale</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close" disabled={deleting}>
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          {/* Names the sale by its figures, not just its id — the amount and
              time are what a cashier recognises. */}
          <p className="pos-delete-lead">
            Delete the <strong>{formatMoney(order?.total, config)}</strong> sale
            {order?.createdAt ? <> from <strong>{posDateTimeText(order.createdAt)}</strong></> : null}?
          </p>
          <p className="pos-delete-note">
            Order {order?.id}{payment ? ` · ${payment}` : ''}{order?.cashier ? ` · ${order.cashier}` : ''}.
            Its status changes to Cancelled/Deleted: it stays on record with your reason, stock is not
            changed, and it no longer counts in the totals, charts or exports. This cannot be undone.
          </p>

          <label className="pos-field">
            <span>Reason for deleting</span>
            <textarea
              rows={3}
              value={reason}
              maxLength={POS_DELETE_REASON_MAX_LENGTH}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer cancelled the order"
              disabled={deleting}
            />
          </label>
          <div className="pos-reason-chips" role="group" aria-label="Common reasons">
            {POS_DELETE_REASON_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`pos-reason-chip${trimmed === preset ? ' active' : ''}`}
                onClick={() => setReason(preset)}
                disabled={deleting}
              >
                {preset}
              </button>
            ))}
          </div>
          <p className="pos-reason-count">
            {trimmed.length < POS_DELETE_REASON_MIN_LENGTH
              ? `Required — at least ${POS_DELETE_REASON_MIN_LENGTH} characters.`
              : `${reason.length}/${POS_DELETE_REASON_MAX_LENGTH}`}
          </p>

          {error && <p className="pos-error">{error}</p>}
          {deleting && <div className="pos-modal-saving">{spinnerRing('Deleting…')}</div>}
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn-danger" onClick={handleDelete} disabled={deleting || !reasonValid}>
            {deleting ? 'Deleting…' : 'Delete sale'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosDeleteSaleModal;
